import * as THREE from 'three';
import type { ContentRegistry } from '../content/registry';
import { clear, h } from '../ui/dom';
import { commitSeconds, PORTAL_BENEFITS, PORTAL_RADIUS, tallyPortals, type PortalKind, type PortalState } from '../sim/portals';
import type { Sim } from '../sim/sim';
import { wrapDelta } from '../sim/world';
import type { Renderer } from './renderer';

/**
 * In-level portal visuals (src/sim/portals.ts owns the rules): an upright ring
 * in the destination era's accent color around a swirling vortex, a ground
 * rune circle that fills as the party's countdown runs, spiralling motes
 * (quality-scaled), and a DOM label with the era, route risk and votes.
 * Presentation only — nothing here feeds back into the sim.
 */

const KIND_TINT: Record<PortalKind, number> = { stage: 0xffffff, stable: 0xffffff, fractured: 0xffb347, volatile: 0xff4d6d };
const KIND_LABEL: Record<PortalKind, string> = { stage: 'Next stage', stable: 'Stable route', fractured: 'Fractured shortcut', volatile: 'Volatile shortcut' };
const MOTES: Record<string, number> = { low: 0, med: 28, high: 56 };
const RING_Y = 1.75;

const vortexVertex = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const vortexFragment = /* glsl */ `
uniform float uTime; uniform vec3 uColor; uniform vec3 uTint; uniform float uIntensity; uniform float uCharge;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  if (r > 1.0) discard;
  float a = atan(p.y, p.x);
  float swirl = sin(a * 3.0 + r * 9.0 - uTime * (2.2 + uCharge * 3.0));
  float bands = 0.5 + 0.5 * swirl;
  float core = smoothstep(0.55, 0.0, r);
  vec3 col = mix(uColor, uTint, 0.35 + 0.25 * swirl) * (0.35 + 0.65 * bands) + vec3(1.0) * core * (0.35 + uCharge * 0.5);
  float alpha = (0.55 + 0.45 * bands) * smoothstep(1.0, 0.8, r);
  gl_FragColor = vec4(col * uIntensity, alpha * uIntensity);
}`;

const runeFragment = /* glsl */ `
uniform float uTime; uniform vec3 uColor; uniform float uProgress; uniform float uIntensity;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float ring = smoothstep(0.78, 0.82, r) * smoothstep(1.0, 0.96, r);
  float inner = smoothstep(0.6, 0.62, r) * smoothstep(0.66, 0.64, r);
  float a = atan(-p.x, p.y) / 6.2831853 + 0.5;
  float filled = step(a, uProgress) * ring;
  float ticks = step(0.8, fract(a * 24.0 + uTime * 0.2)) * inner;
  vec3 col = uColor * (0.35 * ring + 0.6 * ticks) + vec3(1.0) * filled;
  float alpha = max(ring * 0.45, max(filled, ticks * 0.8));
  if (alpha < 0.01) discard;
  gl_FragColor = vec4(col * uIntensity, alpha * uIntensity);
}`;

interface PortalView {
  group: THREE.Group;
  ring: THREE.Mesh;
  vortex: THREE.Mesh<THREE.CircleGeometry, THREE.ShaderMaterial>;
  rune: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  orbiters: THREE.Mesh[];
  preview: THREE.Group;
  motes: THREE.Points | null;
  label: HTMLElement;
  key: string;
}

export class PortalViews {
  private views: PortalView[] = [];
  private labels: HTMLElement;
  private time = 0;
  private opened = 0;

  constructor(private scene: THREE.Scene, private renderer: Renderer, private reg: ContentRegistry, overlayRoot?: HTMLElement) {
    this.labels = h('div', { class: 'portal-labels', 'aria-live': 'polite' });
    (overlayRoot ?? document.body).appendChild(this.labels);
  }

  update(sim: Sim | null, dt: number, camX: number, camZ: number): void {
    this.time += dt;
    const r = sim?.run;
    const active = !!sim && !!r && sim.isClearPhase(r.phase) && r.portals.length > 0;
    if (!active) {
      if (this.views.length) this.clearAll();
      this.opened = 0;
      return;
    }
    const half = sim.world.half;
    const key = r.portals.map((p) => `${p.era}:${p.kind}:${p.x.toFixed(2)}:${p.y.toFixed(2)}`).join('|');
    if (this.views.map((v) => v.key).join('|') !== key) this.build(r.portals);
    this.opened = Math.min(1, this.opened + dt / 0.8);
    const tally = tallyPortals(r.portals, sim.world.players, r.portalIdle, half);
    const total = commitSeconds(sim.config.playerCount);
    const intensity = this.renderer.reducedFlash ? 0.6 : 1;
    const yaw = this.renderer.camYaw;
    const w = innerWidth;
    const hgt = innerHeight;
    const v3 = new THREE.Vector3();
    r.portals.forEach((portal, i) => {
      const view = this.views[i];
      const x = camX + wrapDelta(portal.x - camX, half);
      const z = camZ + wrapDelta(portal.y - camZ, half);
      const charge = r.portalLeader === i ? Math.max(0, Math.min(1, 1 - r.portalCountdown / total)) : 0;
      const grow = easeOutBack(this.opened);
      view.group.position.set(x, 0, z);
      view.group.scale.setScalar(Math.max(0.001, grow));
      // Face the camera so the vortex always reads as a doorway.
      view.ring.parent!.rotation.y = yaw;
      view.ring.rotation.z = this.time * 0.25;
      view.preview.position.y = Math.sin(this.time * 0.8 + i) * 0.04;
      view.preview.rotation.y = -yaw + Math.sin(this.time * 0.35 + i) * 0.08;
      view.orbiters.forEach((o, k) => {
        const a = this.time * (1.1 + charge * 2) + (k * Math.PI * 2) / view.orbiters.length;
        o.position.set(Math.cos(a) * 1.55, RING_Y + Math.sin(a) * 1.55, 0);
        o.rotation.set(this.time * 2, this.time * 1.3, 0);
      });
      const vu = view.vortex.material.uniforms;
      vu.uTime.value = this.time;
      vu.uCharge.value = charge;
      vu.uIntensity.value = intensity;
      const ru = view.rune.material.uniforms;
      ru.uTime.value = this.time;
      ru.uProgress.value = charge;
      ru.uIntensity.value = intensity * (0.7 + 0.3 * Math.sin(this.time * 3));
      if (view.motes) {
        view.motes.rotation.z = -this.time * (0.8 + charge * 2.5);
        const s = 1 - ((this.time * 0.35) % 1) * 0.35;
        view.motes.scale.setScalar(s);
      }
      // Label above the ring.
      this.renderer.project(x, RING_Y * 2 + 0.9, z, v3);
      const visible = v3.z < 1;
      view.label.style.display = visible ? '' : 'none';
      if (visible) view.label.style.transform = `translate(${((v3.x * 0.5 + 0.5) * w).toFixed(0)}px, ${((-v3.y * 0.5 + 0.5) * hgt).toFixed(0)}px) translate(-50%, -100%)`;
      const votes = view.label.querySelector('.portal-votes') as HTMLElement;
      const status = view.label.querySelector('.portal-status') as HTMLElement;
      const occupants = tally.votes[i] ?? 0;
      if (votes) votes.textContent = sim.config.playerCount > 1 ? `${'●'.repeat(occupants)}${'○'.repeat(Math.max(0, tally.required - occupants))}` : '';
      if (status) status.textContent = r.portalLeader === i ? `Travelling in ${Math.max(0, r.portalCountdown).toFixed(1)}s` : occupants > 0 ? `Need ${tally.required - occupants} more` : '';
      view.label.classList.toggle('leading', r.portalLeader === i);
    });
  }

  private build(portals: readonly PortalState[]): void {
    this.clearAll();
    const quality = this.renderer.quality;
    for (const portal of portals) {
      const era = this.reg.eras[portal.era];
      const color = new THREE.Color(era?.palette.accent ?? 0x7df9ff);
      const tint = new THREE.Color(KIND_TINT[portal.kind]);
      const group = new THREE.Group();
      const facing = new THREE.Group();
      group.add(facing);

      // A grounded architectural frame makes the doorway read as a place in
      // the world, while the small era diorama previews its destination.
      const stone = new THREE.MeshStandardMaterial({ color: tint.clone().lerp(new THREE.Color(0x17202b), 0.72), roughness: 0.7, metalness: 0.25 });
      const plinth = new THREE.Mesh(new THREE.CylinderGeometry(1.85, 2.1, 0.22, 12), stone);
      plinth.position.y = 0.08;
      group.add(plinth);
      for (const side of [-1, 1]) {
        const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.25, 1.25, 0.32), stone);
        pillar.position.set(side * 1.55, 0.7, 0);
        pillar.rotation.z = side * -0.1;
        facing.add(pillar);
      }

      const ringMat = new THREE.MeshStandardMaterial({ color: 0x1a2330, emissive: color, emissiveIntensity: 1.6, metalness: 0.6, roughness: 0.35 });
      const ring = new THREE.Mesh(new THREE.TorusGeometry(1.4, 0.13, 14, 64), ringMat);
      ring.position.y = RING_Y;
      facing.add(ring);
      const trim = new THREE.Mesh(new THREE.TorusGeometry(1.62, 0.04, 8, 64), new THREE.MeshBasicMaterial({ color: tint.clone().lerp(color, 0.4) }));
      trim.position.y = RING_Y;
      facing.add(trim);

      const vortexMat = new THREE.ShaderMaterial({
        vertexShader: vortexVertex,
        fragmentShader: vortexFragment,
        uniforms: { uTime: { value: 0 }, uColor: { value: color }, uTint: { value: tint }, uIntensity: { value: 1 }, uCharge: { value: 0 } },
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      });
      const vortex = new THREE.Mesh(new THREE.CircleGeometry(1.32, 48), vortexMat);
      vortex.position.y = RING_Y;
      facing.add(vortex);

      const preview = destinationPreview(portal.era, color, tint);
      preview.position.set(0, 0.7, 0.04);
      preview.scale.setScalar(0.72);
      facing.add(preview);

      const orbiters: THREE.Mesh[] = [];
      const orbGeo = new THREE.OctahedronGeometry(0.13);
      const orbMat = new THREE.MeshBasicMaterial({ color: tint.clone().lerp(color, 0.5) });
      for (let k = 0; k < (quality === 'low' ? 2 : 4); k++) {
        const o = new THREE.Mesh(orbGeo, orbMat);
        facing.add(o);
        orbiters.push(o);
      }

      const runeMat = new THREE.ShaderMaterial({
        vertexShader: vortexVertex,
        fragmentShader: runeFragment,
        uniforms: { uTime: { value: 0 }, uColor: { value: color }, uProgress: { value: 0 }, uIntensity: { value: 1 } },
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const rune = new THREE.Mesh(new THREE.PlaneGeometry(PORTAL_RADIUS * 2.2, PORTAL_RADIUS * 2.2), runeMat);
      rune.rotation.x = -Math.PI / 2;
      rune.position.y = 0.04;
      group.add(rune);

      let motes: THREE.Points | null = null;
      const count = MOTES[quality] ?? 0;
      if (count > 0) {
        const pos = new Float32Array(count * 3);
        for (let k = 0; k < count; k++) {
          const a = (k / count) * Math.PI * 2 * 3;
          const rr = 0.4 + (k / count) * 1.1;
          pos[k * 3] = Math.cos(a) * rr;
          pos[k * 3 + 1] = Math.sin(a) * rr;
          pos[k * 3 + 2] = 0.05;
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        motes = new THREE.Points(geo, new THREE.PointsMaterial({ color: tint.clone().lerp(color, 0.3), size: 0.09, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }));
        motes.position.y = RING_Y;
        facing.add(motes);
      }

      const skipped = portal.kind === 'fractured' ? 1 : portal.kind === 'volatile' ? 2 : 0;
      const route = portal.kind === 'stage' ? 'Continue this era' : skipped === 0 ? 'Normal progression' : `Skip ${skipped} era${skipped > 1 ? 's' : ''} · danger scales now · skipped anchors & loot forfeited`;
      const benefit = PORTAL_BENEFITS[portal.kind];
      const label = h('div', { class: `portal-label portal-${portal.kind}`, style: `--portal-color:#${color.getHexString()}` },
        h('small', null, KIND_LABEL[portal.kind]),
        h('strong', null, portal.kind === 'stage' ? 'Next stage' : era?.name ?? 'Unknown era'),
        h('span', { class: 'portal-detail' }, route),
        h('span', { class: 'portal-benefit' }, `${benefit.title} · ${benefit.detail}`),
        h('span', { class: 'portal-votes' }),
        h('span', { class: 'portal-status' }),
      );
      this.labels.appendChild(label);
      this.scene.add(group);
      this.views.push({ group, ring, vortex, rune, orbiters, preview, motes, label, key: `${portal.era}:${portal.kind}:${portal.x.toFixed(2)}:${portal.y.toFixed(2)}` });
    }
  }

  private clearAll(): void {
    for (const v of this.views) {
      this.scene.remove(v.group);
      v.group.traverse((o) => {
        const mesh = o as THREE.Mesh;
        mesh.geometry?.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat?.dispose();
      });
    }
    this.views = [];
    clear(this.labels);
  }
}

/** Tiny code-native destination landmarks, deliberately replaceable by later authored assets. */
function destinationPreview(era: number, color: THREE.Color, tint: THREE.Color): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.MeshBasicMaterial({ color: color.clone().lerp(tint, 0.25), transparent: true, opacity: 0.72, depthWrite: false });
  const dark = new THREE.MeshBasicMaterial({ color: 0x07101a, transparent: true, opacity: 0.78, depthWrite: false });
  const add = (geometry: THREE.BufferGeometry, x: number, y: number, scale = 1, mat = material) => {
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.position.set(x, y, 0);
    mesh.scale.setScalar(scale);
    group.add(mesh);
  };
  // Ten compact silhouettes: cells, reef, saurian spires, ice, hearth,
  // obelisk, keep, smokestacks, neon towers and the final hourglass.
  switch (era) {
    case 0: add(new THREE.SphereGeometry(0.28, 10, 8), -0.35, 0.5); add(new THREE.SphereGeometry(0.18, 8, 6), 0.32, 0.28); break;
    case 1: add(new THREE.ConeGeometry(0.36, 0.9, 7), 0, 0.45); add(new THREE.SphereGeometry(0.18, 8, 6), -0.48, 0.22); break;
    case 2: add(new THREE.ConeGeometry(0.2, 1.35, 5), -0.38, 0.68); add(new THREE.ConeGeometry(0.28, 1.7, 5), 0.25, 0.85); break;
    case 3: add(new THREE.ConeGeometry(0.42, 1.35, 6), -0.3, 0.68); add(new THREE.ConeGeometry(0.3, 1.05, 6), 0.4, 0.52); break;
    case 4: add(new THREE.ConeGeometry(0.26, 0.95, 8), 0, 0.48); add(new THREE.TorusGeometry(0.35, 0.08, 6, 12), 0, 0.3, 1, dark); break;
    case 5: add(new THREE.BoxGeometry(0.42, 1.45, 0.32), 0, 0.72); add(new THREE.ConeGeometry(0.42, 0.42, 4), 0, 1.55); break;
    case 6: add(new THREE.BoxGeometry(1.25, 0.75, 0.3), 0, 0.38, 1, dark); add(new THREE.ConeGeometry(0.33, 0.8, 4), -0.38, 1.0); add(new THREE.ConeGeometry(0.33, 0.8, 4), 0.38, 1.0); break;
    case 7: add(new THREE.CylinderGeometry(0.18, 0.24, 1.25, 8), -0.4, 0.62); add(new THREE.CylinderGeometry(0.14, 0.2, 1.65, 8), 0.35, 0.82); break;
    case 8: add(new THREE.BoxGeometry(0.32, 1.4, 0.25), -0.36, 0.7); add(new THREE.BoxGeometry(0.38, 1.85, 0.25), 0.25, 0.92); break;
    default: add(new THREE.ConeGeometry(0.5, 0.75, 10), 0, 1.2); add(new THREE.ConeGeometry(0.5, 0.75, 10), 0, 0.45); break;
  }
  return group;
}

function easeOutBack(t: number): number {
  const c = 1.6;
  const x = t - 1;
  return 1 + (c + 1) * x * x * x + c * x * x;
}
